import { useEffect, useState } from "react";
import type { VenueHudProps } from "../types";
import { AimControl, PowerMeter, SportsButtons, TennisAimLine, TennisHelp, WalkPause, useSportsControls } from "./controls";
import { sportsHudView } from "./hudModel";
import { OutcomeBanner } from "./OutcomeBanner";
import "./sports.css";

/** Notices older than this fade from the overlay. */
const NOTICE_MS = 9000;

/**
 * Townsy Sports Center controls (ADR-0016): the buttons that apply where you stand (basketball, soccer, tennis) with
 * their keyboard keys (F / G / R), the power meter (hold to charge, let go to shoot or kick), the soccer aim, the
 * tennis aim and timing cue, your score lines and the latest notices. Every button sends an intent; the server
 * decides what happens. Big buttons on a phone.
 */
export function SportsHud({ shared, mine, send, localActorId, events }: VenueHudProps) {
  const view = sportsHudView(shared, mine, localActorId);
  const controls = useSportsControls(view, send);
  const [now, setNow] = useState(() => Date.now());
  const latest = events.length ? events[events.length - 1]! : null;
  const fresh = events.filter((e) => now - e.at < NOTICE_MS).slice(-2);

  // Re-render while a notice is showing so it fades on time.
  useEffect(() => {
    if (!latest) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    const stop = window.setTimeout(() => window.clearInterval(id), NOTICE_MS + 1000);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(stop);
    };
  }, [latest]);

  return (
    <div className="venue-hud-body sports-hud" data-testid="sports-hud" data-zone={view.zone ?? "none"}>
      <WalkPause controls={controls} />
      <OutcomeBanner events={events} localActorId={localActorId} />
      <div className="sports-hud-head">
        <strong data-testid="sports-hud-title">{view.title}</strong>
        <span className="small muted">Free to play</span>
      </div>
      {view.lines.length ? (
        <ul className="sports-hud-lines" data-testid="sports-hud-lines">
          {view.lines.map((l, i) => (
            <li key={i} className={i === 0 ? "sports-hud-score" : undefined}>
              {l}
            </li>
          ))}
        </ul>
      ) : null}
      {view.help ? <TennisHelp text={view.help} /> : null}
      {view.buttons.length ? <SportsButtons view={view} controls={controls} /> : null}
      {view.meter ? <PowerMeter kind={view.meter} /> : null}
      {view.aimButtons ? <AimControl /> : null}
      {view.mySide !== null ? <TennisAimLine side={view.mySide} /> : null}
      {view.buttons.some((b) => b.disabledReason) ? (
        <p className="small muted sports-hud-why" data-testid="sports-hud-why">
          {view.buttons.find((b) => b.disabledReason)!.disabledReason}
        </p>
      ) : null}
      <p className={`small muted sports-hud-hint${view.buttons.length ? "" : " guide"}`}>{view.hint}</p>
      <div className="sports-hud-notices" aria-live="polite" data-testid="sports-hud-notices">
        {fresh.map((e) => (
          <p key={e.id} className={`sports-hud-notice${e.type === "rejected" ? " rejected" : e.type === "result" ? " result" : ""}`}>
            {e.message}
          </p>
        ))}
      </div>
    </div>
  );
}
